import React, { useMemo, useState, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  MDBContainer,
  MDBBtn,
  MDBIcon,
} from "mdb-react-ui-kit";
import { formattedNumber } from "../popups/phonenumbers/AddRemoveNumbers";
import { customSearchNumbers } from "../../components/CustomSearchUtil/CustomSearch";

const SMSCampaignPhoneNumber = React.memo(({ phoneNumList, defaultBulkMode, draftBulkPhnums = [] }) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    register,
    clearErrors,
  } = useFormContext();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isShake, setIsShake] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkError, setBulkError] = useState("");
  const bulkTextRef = useRef("");

  // Restore bulk mode and textarea when loading from draft.
  // Use draftBulkPhnums prop directly — avoids timing issue where getValues()
  // would return [] because parent's setValue hasn't run yet at mount time.
  useEffect(() => {
    if (defaultBulkMode && draftBulkPhnums.length > 0) {
      setIsBulkMode(true);
      const text = draftBulkPhnums.join("\n");
      setBulkText(text);
      bulkTextRef.current = text;
    }
  }, [defaultBulkMode, draftBulkPhnums]);

  const perPage = 24;
  const maxPhones = 49;

  const selectedPhones = watch("ph_numbers.add_phnums") || [];

  useEffect(() => {
    if (selectedPhones.length > 0) {
      const validPhones = selectedPhones.filter((phone) =>
        phoneNumList.includes(phone)
      );
      if (validPhones.length !== selectedPhones.length) {
        setValue("ph_numbers.add_phnums", validPhones, { shouldDirty: true });
      }
    }
  }, [phoneNumList, selectedPhones, setValue]);

  const validSelectedPhones = useMemo(() => {
    return selectedPhones.filter((phone) => phoneNumList.includes(phone));
  }, [selectedPhones, phoneNumList]);

  const filtered = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return customSearchNumbers(phoneNumList, searchTerm);
  }, [phoneNumList, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageSafe = Math.min(page, totalPages);
  const slice = filtered.slice(
    (pageSafe - 1) * perPage,
    (pageSafe - 1) * perPage + perPage
  );

  useEffect(() => {
    setPage(1);
  }, [search]);

  const toggleNumber = (phoneNumber) => {
    clearErrors("ph_numbers.add_phnums");
    const currentSelected = selectedPhones || [];
    const isSelected = currentSelected.includes(phoneNumber);

    if (isSelected) {
      setValue(
        "ph_numbers.add_phnums",
        currentSelected.filter((phone) => phone !== phoneNumber),
        { shouldDirty: true }
      );
    } else {
      if (currentSelected.length >= maxPhones) {
        setIsShake(true);
        setTimeout(() => setIsShake(false), 600);
        return;
      }
      setValue("ph_numbers.add_phnums", [...currentSelected, phoneNumber], {
        shouldDirty: true,
      });
    }
  };

  const handleClearAll = () => {
    if (isBulkMode) {
      clearErrors("ph_numbers.bulk_phnums");
      setValue("ph_numbers.bulk_phnums", [], { shouldDirty: true });
      setBulkText("");
      setBulkError("");
    } else {
      clearErrors("ph_numbers.add_phnums");
      setValue("ph_numbers.add_phnums", [], { shouldDirty: true });
    }
  };

  // Normalize any formatted phone token to pure digits
  const normalizePhoneToken = (token) => token.replace(/[+\-\(\)\s]/g, "");

  // Pure parser — returns { error: string|null, numbers: string[] }
  const parseBulkNumbers = (text) => {
    if (!text || !text.trim()) {
      return { error: "Please enter at least one valid phone number", numbers: [] };
    }

    // Only allow digits, +, -, (, ), spaces, comma, newline
    if (!/^[0-9+\-\(\)\s,\n\r]*$/.test(text)) {
      return { error: "Kindly enter valid phone number(s).", numbers: [] };
    }

    // Split ONLY by comma or newline
    const tokens = text.split(/[,\n\r]+/).map((t) => t.trim()).filter((t) => t.length > 0);

    const invalidDid = [];
    const didArray = [];

    tokens.forEach((token) => {
      // If token has internal spaces it must start with + (e.g. +1 910 356 9057)
      if (/\s/.test(token) && !token.startsWith("+")) {
        invalidDid.push(token);
        return;
      }

      const digits = normalizePhoneToken(token);

      if (!/^[0-9]+$/.test(digits) || digits.length === 0) {
        invalidDid.push(token);
        return;
      }

      if (digits.length < 10 || digits.length > 11) {
        invalidDid.push(token);
        return;
      }

      if (digits.length === 11) {
        if (digits.charAt(0) !== "1") {
          invalidDid.push(token);
        } else {
          didArray.push(digits);
        }
      } else {
        // 10 digits — prepend country code 1
        didArray.push("1" + digits);
      }
    });

    if (invalidDid.length > 0) {
      const framedDid = invalidDid.map((v, i) => (i !== 0 ? " " + v : v)).join(",");
      return {
        error: `The following number(s) ${framedDid} are invalid. Please remove them and resubmit your request with valid phone numbers.`,
        numbers: [],
      };
    }

    const unique = [...new Set(didArray)];
    if (unique.length > maxPhones) {
      return { error: "The maximum limit of 49 phone numbers have been exceeded.", numbers: [] };
    }
    return { error: null, numbers: unique };
  };

  const handleBulkInput = (e) => {
    const text = e.target.value;
    setBulkText(text);
    bulkTextRef.current = text;
    // Clear stale error while user is editing — error display fires on submit
    setBulkError("");
    clearErrors("ph_numbers.bulk_phnums");
    // Keep bulk_phnums in sync so the submitted payload always has the right value
    if (!text || !text.trim()) {
      setValue("ph_numbers.bulk_phnums", [], { shouldDirty: true });
      return;
    }
    const result = parseBulkNumbers(text);
    setValue(
      "ph_numbers.bulk_phnums",
      result.error ? [] : result.numbers,
      { shouldDirty: true }
    );
  };

  const handleToggleBulkMode = () => {
    if (isBulkMode) {
      // Switching to grid — clear bulk data
      setValue("ph_numbers.bulk_phnums", [], { shouldDirty: true });
      setBulkText("");
      clearErrors("ph_numbers.bulk_phnums");
      setBulkError("");
    } else {
      // Switching to bulk — clear grid selection
      setValue("ph_numbers.add_phnums", [], { shouldDirty: true });
    }
    setIsBulkMode((prev) => !prev);
  };

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <MDBContainer className="py-4">
      {/* Header row: title + toggle button */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        {isBulkMode
          ? <h4 className="mb-0">Bulk Phone Number Update</h4>
          : <h4 className="mb-0">Select Phone Numbers</h4>
        }
        <MDBBtn
          size="sm"
          color="primary"
          onClick={handleToggleBulkMode}
        >
          {isBulkMode ? (
            <>
              <MDBIcon fas icon="th" className="me-1" /> Select Phone Numbers
            </>
          ) : (
            <>
              <MDBIcon fas icon="upload" className="me-1" /> Bulk Phone Number Update
            </>
          )}
        </MDBBtn>
      </div>

      {/* Selected chips — hidden in bulk mode */}
      {!isBulkMode && <div className="bg-secondary p-3 rounded-3 bg-opacity-10 border border-color-secondary border-2 mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <label className="message-label m-0 fw-semibold">
            Selected Phone Numbers ({validSelectedPhones.length}/{maxPhones}):
          </label>
          {validSelectedPhones.length > 0 && (
            <span
              className="text-primary"
              style={{ cursor: "pointer", fontSize: "0.875rem" }}
              onClick={handleClearAll}
            >
              Clear all
            </span>
          )}
        </div>
        <div  className={`numbers-grid p-2 rounded-3 m-0 ${ validSelectedPhones.length > 0 ? "border bg-white shadow-sm" : ""
              }`} style={{ overflow: "visible", maxHeight: "none" }}>
          {validSelectedPhones.length === 0 ? (
            <div className="text-muted w-100 text-center py-2" style={{ gridColumn: "1 / -1" }}>No phone numbers selected</div>
          ) : (
            validSelectedPhones.map((phone, index) => (
              <div
                key={index}
                className="number-card position-relative"
                style={{
                  cursor: "pointer",
                  paddingRight: "2.1rem",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  color: "#212529",
                  letterSpacing: "normal"
                }}
              >
                {formattedNumber(phone)}
                <span
                  className="position-absolute top-50 end-0 translate-middle-y me-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNumber(phone);
                  }}
                  style={{
                    cursor: "pointer",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: "#000",
                    lineHeight: "1",
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#dc3545";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#000";
                  }}
                >
                  ×
                </span>
              </div>
            ))
          )}
        </div>
      </div>}

      {/* Hidden controllers for validation — one per mode */}
      {isBulkMode ? (
        <Controller
          name="ph_numbers.bulk_phnums"
          control={control}
          rules={{
            validate: (value) => {
              if (!value || value.length === 0) {
                // Parse to surface the specific error message (e.g. which numbers are invalid)
                const result = parseBulkNumbers(bulkTextRef.current);
                const msg = result.error || "Please enter at least one valid phone number";
                setBulkError(msg);
                return msg;
              }
              setBulkError("");
              return true;
            },
          }}
          render={() => null}
        />
      ) : (
        <Controller
          name="ph_numbers.add_phnums"
          control={control}
          rules={{
            validate: (value) => {
              if (!value || value.length === 0) {
                return "Please select at least one phone number";
              }
              return true;
            },
          }}
          render={() => null}
        />
      )}
      {!isBulkMode && errors?.ph_numbers?.add_phnums && (
        <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.75rem" }}>
          {errors.ph_numbers.add_phnums.message}
        </div>
      )}

      {/* Bulk mode: textarea */}
      {isBulkMode ? (
        <div className="bg-secondary p-3 rounded-3 bg-opacity-10 border border-color-secondary border-2 shadow-3-strong mt-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <label className="message-label fw-semibold mb-0">
              Paste Numbers for Bulk Selection
            </label>
            {/* <span className="text-muted" style={{ fontSize: "0.8rem" }}>
              Comma or line separated
            </span> */}
          </div>
          <textarea
            className="form-control shadow-sm"
            rows={8}
            placeholder={`Kindly enter SMS campaign numbers.`}
            value={bulkText}
            onChange={handleBulkInput}
            style={{ resize: "vertical", fontSize: "0.875rem" }}
          />
          {(bulkError || errors?.ph_numbers?.bulk_phnums) && (
           
            <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.75rem" }}>
           {bulkError || errors?.ph_numbers?.bulk_phnums?.message}
        </div>
          )}

          <p className="phrecording text-muted  mb-0">
            <strong>Note: </strong>
            You can add a maximum of {maxPhones} phone numbers to a campaign. Please enter the phone numbers separated by a space, comma or on a new line.
          </p>
        </div>
      ) : (
        <>
          {/* Grid mode: search + grid + pagination */}
          <div className="biz-form m-0 p-0 d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <div className="form-group mb-0 m-0 w-100">
              <div className="input-wrapper">
                <input
                  id="phone_search"
                  placeholder="Search phone numbers"
                  value={search}
                  autoComplete="phone-search"
                  onChange={(e) => setSearch(e.target.value)}
                />
                <svg
                  className="input-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <section className={isShake ? "shake" : ""}>
            <div className="numbers-grid mb-3">
              {slice.map((phoneNumber) => {
                const isSelected = selectedPhones.includes(phoneNumber);
                const isDisabled =
                  !isSelected && selectedPhones.length >= maxPhones;
                return (
                  <div
                    key={phoneNumber}
                    className={`number-card ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                    onClick={() => !isDisabled && toggleNumber(phoneNumber)}
                  >
                    {formattedNumber(phoneNumber)}
                  </div>
                );
              })}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-4">
                <MDBIcon fas icon="search" size="2x" className="text-muted mb-2" />
                <p className="text-muted">
                  No phone numbers found
                  {search && ` matching "${search}"`}
                </p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="phone-pagination d-flex flex-column align-items-center gap-2">
                <div className="text-muted small">
                  Page {pageSafe} of {totalPages} ({filtered.length} phone numbers)
                </div>
                <div className="d-flex justify-content-center gap-2">
                  <MDBBtn
                    color="light"
                    size="sm"
                    onClick={goPrev}
                    disabled={pageSafe === 1}
                  >
                    <MDBIcon fas icon="chevron-left" />
                  </MDBBtn>

                  {(() => {
                    const maxVisiblePages = Math.min(8, totalPages);
                    const half = Math.floor(maxVisiblePages / 2);
                    let start = Math.max(1, pageSafe - half);
                    let end = Math.min(totalPages, start + maxVisiblePages - 1);
                    if (end - start + 1 < maxVisiblePages) {
                      start = Math.max(1, end - maxVisiblePages + 1);
                    }
                    return Array.from(
                      { length: end - start + 1 },
                      (_, i) => start + i
                    ).map((pageNum) => (
                      <MDBBtn
                        key={pageNum}
                        size="sm"
                        color={pageNum === pageSafe ? "primary" : "secondary"}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </MDBBtn>
                    ));
                  })()}

                  <MDBBtn
                    color="light"
                    size="sm"
                    onClick={goNext}
                    disabled={pageSafe === totalPages}
                  >
                    <MDBIcon fas icon="chevron-right" />
                  </MDBBtn>
                </div>
              </div>
            )}
          </section>

          <div className="mt-3">
            <MDBIcon fas icon="info-circle" className="me-2" color="secondary" />
            You can select up to {maxPhones} phone numbers
          </div>
        </>
      )}
    </MDBContainer>
  );
});

SMSCampaignPhoneNumber.displayName = "SMSCampaignPhoneNumber";

export default SMSCampaignPhoneNumber;